import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, Tooltip, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from "recharts";
import Switch from "react-switch";
import { formatIndianStyle, getTimestamp } from "../helper/arrayFormatter";
import html2pdf from 'html2pdf.js';
import CustomButton from "./CustomButton";

const MakeGraph = ({ data, Title = "Total Usage", hideToggle = false, isDownloadPdf = false, isSecond = false, sort = true }) => {
	const [chartData, setChartData] = useState([]);
	const [chartType, setChartType] = useState('bar'); // State to track the chart type (either 'bar' or 'line')
	const FILL_COLOR = "rgb(59, 130, 246)";
	const [IsDownload, setIsDownload] = useState(false)

	// Convert large numbers to human-readable format (e.g., 40000000 -> 4M)
	const formatValue = (value) => {
		if (typeof value === 'number') {
			if (value >= 1e9) {
				return (value / 1e9).toFixed(1) + 'B'; // Billion
			} else if (value >= 1e6) {
				return (value / 1e6).toFixed(1) + 'M'; // Million
			} else if (value >= 1e3) {
				return (value / 1e3).toFixed(1) + 'K'; // Thousand
			} else {
				return value.toString(); // If less than 1000, return the value as is
			}
		}
		return value;
	};

	useEffect(() => {
		if (!data) {
			return;
		}

		let entries = Object.entries(data); // Convert to array of key-value pairs

		// Convert string values to float
		entries = entries.map(([key, value]) => {
			if (typeof value === 'string') {
				// Remove commas and convert to float if possible
				value = value.replace(/,/g, '');
				if (!isNaN(value)) {
					value = parseFloat(value);
				}
			}
			return { name: key, value };
		});

		// Assuming 'orderlist' is an array of key names in the order you want
		let orderlist = [
			"total_pax",
			"dy_pax",
			"pax_pesc",
			"pax_boarding",
			"entry_via_app",
			"entry_kiosk",
		];

		let ignoreFields = ["Airport", "Airline", "dy_diff", "dy_perc", "Latestdate"]; // Specify fields to ignore

		// Create a map of entries for faster lookup
		let entriesMap = new Map(entries.map(entry => [entry.name, entry]));

		// Remove the ignore fields if they exist in the entries map
		ignoreFields.forEach(field => {
			if (entriesMap.has(field)) {
				entriesMap.delete(field);
			}
		});

		// Reorder entries based on the orderlist
		let reorderedEntries = orderlist
			.map(key => entriesMap.get(key)) // First try to find from entriesMap
			.filter(entry => entry !== undefined); // Remove any undefined values (keys not found)

		// Add any remaining keys that were not in the orderlist
		let remainingEntries = Array.from(entriesMap.values()).filter(entry => !orderlist.includes(entry.name));
		reorderedEntries = reorderedEntries.concat(remainingEntries);

		if (sort) {
			reorderedEntries.sort((a, b) => {
				return b?.value - a?.value
			})
		}

		setChartData(reorderedEntries);
	}, [data]);

	const downloadPageAsPdf = (val) => {
		setIsDownload(true)
		const element = document.getElementById(val);

		const options = {
			image: { type: 'jpeg', quality: 1 },
			html2canvas: {
				scale: 3,
				letterRendering: true,
				logging: false
			},
			jsPDF: {
				unit: 'mm',
				format: 'a4',
				orientation: 'landscape'
			}
		};

		const timestamp = getTimestamp();
		const filename = `DY-Graph_${timestamp}.pdf`;

		// Generate the PDF
		html2pdf()
			.from(element)
			.set(options)
			.save(filename);

		setTimeout(() => {
			setIsDownload(false)
		}, 500);
	};

	function RenderToggle() {
		return (
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 60 }}>
				<label style={{ marginRight: "8px", fontSize: '13px', color: chartType === 'bar' ? FILL_COLOR : '#333', fontWeight: chartType === 'bar' ? "bold" : "normal" }}>Bar</label>
				<Switch uncheckedIcon={false} checkedIcon={false} width={35} height={18}
					onChange={() => setChartType(chartType === 'bar' ? 'line' : 'bar')}
					checked={chartType === 'line'} />
				<label style={{ marginLeft: "8px", fontSize: '13px', color: chartType === 'line' ? FILL_COLOR : '#333', fontWeight: chartType === 'line' ? "bold" : "normal" }}>Line</label>
			</div>
		)
	}

	if (!chartData.length) {
		return <div></div>;
	}

	const maxValue = Math.max(...chartData.map(data => data.value));
	const yAxisMaxValue = maxValue * 1.1

	return (
		<div style={{ maxWidth: "1270px", height: "fit-content", border: '1px solid #ddd', borderRadius: '10px', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)' }}>
			<div style={{ display: "flex", justifyContent: "space-between", paddingInline: "10px" }}>
				<h2 style={{
					color: "#FFF", margin: "10px", marginLeft: "20px",
					fontSize: "13px",
					fontWeight: "500",
					padding: "4px 30px",
					borderRadius: '5px 25px',
					background: 'rgb(59 130 246)',
					height: isDownloadPdf ? 35 : 28
				}}>{Title}</h2>

				{/* Toggle Switch */}
				{!IsDownload && !isDownloadPdf && hideToggle && <RenderToggle />}
			</div>

			{chartType === 'bar' ? (
				<div style={{ position: "relative" }}>
					<ResponsiveContainer id="Graph1" width="100%" height={300}>
						<BarChart data={chartData}>
							<CartesianGrid strokeDasharray="2 4" />
							<XAxis
								dataKey="name"
								tick={{ fontSize: 12 }}
								angle={-45}
								textAnchor="end"
								height={70}
							/>
							<YAxis tickFormatter={formatValue} domain={[0, yAxisMaxValue]} />
							<Tooltip formatter={formatIndianStyle} contentStyle={{ borderRadius: 15 }} />
							<Bar dataKey="value" fill={FILL_COLOR}>
								{!(isSecond && chartData?.length > 16) && <LabelList style={{ fontSize: 12 }} dataKey="value" position="top" formatter={formatValue} />}
							</Bar>
						</BarChart>
					</ResponsiveContainer>

					{!IsDownload && !isDownloadPdf && <div style={{ position: "absolute", bottom: 5, left: 5, zIndex: 99 }}>
						<CustomButton icon={<img src={require("../assests/download.png")} alt="download" loading="lazy"
							style={{ width: 15, height: 15 }} />}
							label={"PDF"}
							onClick={() => downloadPageAsPdf("Graph1")}
						/>
					</div>}
				</div>
			) : (
				<div style={{ position: "relative" }}>
					<ResponsiveContainer id="Graph2" width="100%" height={300}>
						<LineChart data={chartData}>
							<CartesianGrid strokeDasharray="2 4" />
							<XAxis
								dataKey="name"
								tick={{ fontSize: 12 }}
								angle={-45}
								textAnchor="end"
								height={70}
							/>
							<YAxis tickFormatter={formatValue} />
							<Tooltip formatter={formatIndianStyle} contentStyle={{ borderRadius: 15 }} />
							<Line type="monotone" dataKey="value" stroke={FILL_COLOR}>
								{!(isSecond && chartData?.length > 16) && <LabelList style={{ fontSize: 12 }} dataKey="value" position="top" formatter={formatValue} />}
							</Line>
						</LineChart>
					</ResponsiveContainer>

					{!IsDownload && !isDownloadPdf && <div style={{ position: "absolute", bottom: 5, left: 5, zIndex: 99 }}>
						<CustomButton icon={<img src={require("../assests/download.png")} alt="download" loading="lazy"
							style={{ width: 15, height: 15 }} />}
							label={"PDF"}
							onClick={() => downloadPageAsPdf("Graph2")}
						/>
					</div>}
				</div>
			)}
		</div>
	);
};

export default MakeGraph;